import React, { useEffect, useMemo, useState } from 'react';
import { type AppDispatch, type RootState } from '@/store';
import {
  Add,
  AddCircle,
  Analytics,
  Calculate,
  CheckCircle,
  Dashboard,
  Delete,
  Description,
  Edit,
  ExpandMore,
  Info,
  Settings,
  Speed,
  Timeline,
  TrendingDown as TrendingDownIcon,
  TrendingFlat,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
  Warning,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import {
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  userLoanEstimationTypeMap,
  userOccupancyMap,
} from '@/config/constants';
import {
  useGetDashboardStatsQuery,
  useGetLatestRatesQuery,
  usePatchBridgeTermsMutation,
  usePatchProgramMatrixCellMutation,
} from '@/apis/extended/admin/interestRateApi';
import type {
  BridgeTerms,
  CalculatedFeeRule,
  FeeAmountType,
  FeeCategory,
  FeeItem,
  FeeSign,
} from '@/apis/extended/admin/interestRateApi';
import { MarketRatesPanel } from '@/components/Admin/InterestRates/MarketRatesPanel';
import {
  clearDraftChanges,
  clearError,
  loadAprAssumptionsFromBackend,
  loadCalculatedFeeRulesFromBackend,
  loadFeeItemsFromBackend,
  loadRateMatricesFromBackend,
  resetRateMatrixAsync,
  selectDraftChanges,
  selectError,
  selectLoading,
  selectRateChangeHistory,
  selectRateMatrices,
  selectSelectedLoanType,
  setActiveConfigurationId,
  setSelectedLoanType,
  updateRateMatrixAsync,
} from '@/store/slices/interestRateSlice';
import type { DraftChange } from '@/store/slices/interestRateSlice';
import {
  calculateAPRWithLoanType,
  LOAN_TYPE_FEES,
  type APRCalculationParams,
  type APRCalculationResult,
} from '@/utils/aprUtils';
import {
  mergeProgramDocumentationRequests,
  type DocumentationRequestsByType,
} from '@/utils/document';
import { CALCULATED_FEE_CATALOG } from '@/utils/fees/calculatedFeeCatalog';
import {
  BACKEND_TO_FRONTEND_LOAN_TYPE,
  RATE_TABLE_CONFIGS,
  transformAllBackendMatrices,
  validateRateMatrix,
  type LoanType,
  type RateMatrix,
} from '@/utils/rateMatrixUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edit-rates-dialog-tabpanel-${index}`}
      aria-labelledby={`edit-rates-dialog-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PROGRAM_KEY_MAP: Record<LoanType, string> = {
  Cash: 'cash',
  DSCR: 'dscr',
  Conventional: 'conventional',
  FHA: 'fha',
  USDA: 'usda',
  VA: 'va',
  Commercial: 'commercial',
  FixAndFlip: 'fixAndFlip',
  Bridge: 'bridge',
  HELOC: 'heloc',
  HELOAN: 'heloan',
  SecondMortgage: 'secondMortgage',
};

const LOAN_TYPE_TO_USER_PROGRAM: Record<LoanType, USER_LOAN_PROGRAM> = {
  Cash: USER_LOAN_PROGRAM.CASH,
  DSCR: USER_LOAN_PROGRAM.DSCR,
  Conventional: USER_LOAN_PROGRAM.CONVENTIONAL,
  FHA: USER_LOAN_PROGRAM.FHA,
  USDA: USER_LOAN_PROGRAM.USDA,
  VA: USER_LOAN_PROGRAM.VA,
  Commercial: USER_LOAN_PROGRAM.COMMERCIAL,
  FixAndFlip: USER_LOAN_PROGRAM.FIX_AND_FLIP,
  Bridge: USER_LOAN_PROGRAM.BRIDGE,
  HELOC: USER_LOAN_PROGRAM.HELOC,
  HELOAN: USER_LOAN_PROGRAM.HELOAN,
  SecondMortgage: USER_LOAN_PROGRAM.SECOND_MORTGAGE,
};

export const AdminInterestRates = () => {
  const dispatch = useDispatch<AppDispatch>();
  const rateMatrices = useSelector(selectRateMatrices);
  const selectedLoanType = useSelector(selectSelectedLoanType);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const rateChangeHistory = useSelector(selectRateChangeHistory);
  const draftChanges = useSelector(selectDraftChanges);

  // API hooks for fetching data from backend
  const { data: latestRates, isLoading: latestRatesLoading } =
    useGetLatestRatesQuery();

  const { data: dashboardStats, isLoading: statsLoading } =
    useGetDashboardStatsQuery();

  // API hooks for mutations
  const [patchProgramMatrixCell] = usePatchProgramMatrixCellMutation();
  const [patchBridgeTerms] = usePatchBridgeTermsMutation();

  const [activeMatrixLoanType, setActiveMatrixLoanType] =
    useState<LoanType>('Conventional');
  const [programModalOpen, setProgramModalOpen] = useState(false);
  const [aprModalOpen, setAprModalOpen] = useState(false);
  const [reasonForChange, setReasonForChange] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [feeItemsEdits, setFeeItemsEdits] = useState<
    Partial<Record<LoanType, FeeItem[]>>
  >({});
  const [feeItemsDirty, setFeeItemsDirty] = useState<
    Partial<Record<LoanType, boolean>>
  >({});
  const [calculatedFeeRulesEdits, setCalculatedFeeRulesEdits] = useState<
    Partial<Record<LoanType, CalculatedFeeRule[]>>
  >({});
  const [calculatedFeeRulesDirty, setCalculatedFeeRulesDirty] = useState<
    Partial<Record<LoanType, boolean>>
  >({});
  const [bridgeTermsEdits, setBridgeTermsEdits] = useState<BridgeTerms | null>(
    null
  );
  const [bridgeTermsDirty, setBridgeTermsDirty] = useState(false);
  const [documentationRequestsEdits, setDocumentationRequestsEdits] = useState<
    Partial<Record<LoanType, DocumentationRequestsByType>>
  >({});
  const [documentationRequestsDirty, setDocumentationRequestsDirty] = useState<
    Partial<Record<LoanType, boolean>>
  >({});
  const [editRatesFolderTab, setEditRatesFolderTab] = useState(0); // 0: rates, 1: closing costs, 2: documentation
  const [feeLibrarySelection, setFeeLibrarySelection] = useState<string>('');
  const [aprCalculationParams, setAprCalculationParams] =
    useState<APRCalculationParams>({
      interestRate: 6.5,
      loanAmount: 300000,
      loanTermMonths: 360,
      originationFees: 0,
      otherFees: 2000,
      loanType: 'Conventional',
    });

  const defaultBridgeTerms: BridgeTerms = {
    sixMonth: 8.99,
    twelveMonth: 9.99,
    eighteenMonth: 11.5,
    twentyFourMonth: 12.99,
    baseRate: 10.5,
    indexType: 'internal_custom',
    indexValue: 7.5,
    spread: 2.5,
  };

  useEffect(() => {
    if (latestRates?._id) {
      dispatch(setActiveConfigurationId(latestRates._id));
      dispatch(clearDraftChanges());
    }
  }, [dispatch, latestRates?._id]);

  // The matrix editor's Redux state starts out seeded with hardcoded
  // frontend defaults (see interestRateSlice's initialState) so it has
  // something to render immediately — without this, it would never
  // reflect what's actually saved, and every reload would silently show
  // the defaults again regardless of what an admin had published.
  useEffect(() => {
    if (!latestRates?.programs) return;

    dispatch(
      loadRateMatricesFromBackend(transformAllBackendMatrices(latestRates))
    );

    const aprByLoanType: Partial<Record<LoanType, Record<string, any>>> = {};
    const feesByLoanType: Partial<Record<LoanType, FeeItem[]>> = {};
    const rulesByLoanType: Partial<Record<LoanType, CalculatedFeeRule[]>> = {};

    Object.entries(BACKEND_TO_FRONTEND_LOAN_TYPE).forEach(
      ([backendKey, loanType]) => {
        const program =
          latestRates.programs[backendKey as keyof typeof latestRates.programs];
        if (!program) return;
        if (program.aprAssumptions)
          aprByLoanType[loanType] = program.aprAssumptions;
        if (program.feeItems) feesByLoanType[loanType] = program.feeItems;
        if (program.calculatedFeeRules) {
          rulesByLoanType[loanType] = program.calculatedFeeRules;
        }
      }
    );

    dispatch(loadAprAssumptionsFromBackend(aprByLoanType));
    dispatch(loadFeeItemsFromBackend(feesByLoanType));
    dispatch(loadCalculatedFeeRulesFromBackend(rulesByLoanType));
  }, [dispatch, latestRates]);

  const handleOpenProgramModal = (loanType: LoanType) => {
    dispatch(setSelectedLoanType(loanType));
    const programKey = PROGRAM_KEY_MAP[loanType];
    const existingFees =
      latestRates?.programs?.[programKey as keyof typeof latestRates.programs]
        ?.feeItems;
    setFeeItemsEdits((prev) => ({
      ...prev,
      [loanType]: Array.isArray(existingFees)
        ? (existingFees as FeeItem[])
        : [],
    }));
    setFeeItemsDirty((prev) => ({ ...prev, [loanType]: false }));

    const existingDocs =
      latestRates?.programs?.[programKey as keyof typeof latestRates.programs]
        ?.documentationRequests;
    const loanProgram = LOAN_TYPE_TO_USER_PROGRAM[loanType];
    const mergedDocs = mergeProgramDocumentationRequests(
      loanProgram,
      existingDocs && typeof existingDocs === 'object'
        ? (existingDocs as DocumentationRequestsByType)
        : undefined
    );
    setDocumentationRequestsEdits((prev) => ({
      ...prev,
      [loanType]: mergedDocs,
    }));
    setDocumentationRequestsDirty((prev) => ({ ...prev, [loanType]: false }));

    const existingRules =
      latestRates?.programs?.[programKey as keyof typeof latestRates.programs]
        ?.calculatedFeeRules;
    const existingRuleMap = new Map(
      (existingRules || []).map((rule: CalculatedFeeRule) => [rule.key, rule])
    );
    const normalizedRules = CALCULATED_FEE_CATALOG.map((catalogRule) => {
      const existing = existingRuleMap.get(catalogRule.key);
      const isCash = loanType === 'Cash';
      const forceHidden = isCash && catalogRule.category === 'lender';
      return {
        key: catalogRule.key,
        label: catalogRule.label,
        category: catalogRule.category,
        description: existing?.description ?? catalogRule.defaultDescription,
        visible: forceHidden
          ? false
          : existing?.visible ?? catalogRule.defaultVisible,
        includedInApr: forceHidden
          ? false
          : existing?.includedInApr ?? catalogRule.defaultIncludedInApr,
      } as CalculatedFeeRule;
    });
    setCalculatedFeeRulesEdits((prev) => ({
      ...prev,
      [loanType]: normalizedRules,
    }));
    setCalculatedFeeRulesDirty((prev) => ({ ...prev, [loanType]: false }));
    if (loanType === 'Bridge') {
      const mergedBridgeTerms: BridgeTerms = {
        ...defaultBridgeTerms,
        ...(latestRates?.bridgeTerms || {}),
      };
      setBridgeTermsEdits(mergedBridgeTerms);
      setBridgeTermsDirty(false);
    }
    setEditRatesFolderTab(0);
    setProgramModalOpen(true);
  };

  const handleUpdateCalculatedRule = (
    loanType: LoanType,
    ruleKey: string,
    patch: Partial<CalculatedFeeRule>
  ) => {
    setCalculatedFeeRulesEdits((prev) => {
      const list = [...(prev[loanType] || [])];
      const idx = list.findIndex((r) => r.key === ruleKey);
      if (idx === -1) return prev;
      list[idx] = { ...list[idx], ...patch };
      return { ...prev, [loanType]: list };
    });
    setCalculatedFeeRulesDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleUpdateDocumentationRequests = (
    loanType: LoanType,
    estimationType: USER_LOAN_ESTIMATION_TYPE,
    occupancy: string,
    value: string
  ) => {
    const lines = Array.from(
      new Set(
        value
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
      )
    );
    setDocumentationRequestsEdits((prev) => ({
      ...prev,
      [loanType]: {
        ...(prev[loanType] || {}),
        [estimationType]: {
          ...((prev[loanType] || {})[estimationType] || {}),
          [occupancy]: lines,
        },
      },
    }));
    setDocumentationRequestsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleResetDocumentationRequests = (
    loanType: LoanType,
    estimationType: USER_LOAN_ESTIMATION_TYPE
  ) => {
    const loanProgram = LOAN_TYPE_TO_USER_PROGRAM[loanType];
    const defaults = mergeProgramDocumentationRequests(loanProgram);
    setDocumentationRequestsEdits((prev) => ({
      ...prev,
      [loanType]: {
        ...(prev[loanType] || {}),
        [estimationType]: defaults[estimationType] || {},
      },
    }));
    setDocumentationRequestsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const getProgramDocumentationRequests = (loanType: LoanType) => {
    const programKey = PROGRAM_KEY_MAP[loanType];
    const stored =
      documentationRequestsEdits[loanType] ||
      latestRates?.programs?.[programKey as keyof typeof latestRates.programs]
        ?.documentationRequests;
    const loanProgram = LOAN_TYPE_TO_USER_PROGRAM[loanType];
    return mergeProgramDocumentationRequests(
      loanProgram,
      stored && typeof stored === 'object'
        ? (stored as DocumentationRequestsByType)
        : undefined
    );
  };

  const getUniqueDocumentationRequests = (
    docsByType: DocumentationRequestsByType
  ) => {
    const unique = new Set<string>();
    Object.values(docsByType).forEach((occupancyMap) => {
      if (!occupancyMap) return;
      Object.values(occupancyMap).forEach((docs) => {
        if (!Array.isArray(docs)) return;
        docs.forEach((doc) => {
          const trimmed = doc.trim();
          if (trimmed.length > 0) unique.add(trimmed);
        });
      });
    });
    return Array.from(unique);
  };

  const handleRemoveDocumentationRequest = (
    loanType: LoanType,
    documentLabel: string
  ) => {
    const docsByType = getProgramDocumentationRequests(loanType);
    const next: DocumentationRequestsByType = {};
    Object.entries(docsByType).forEach(([estimationType, occupancyMap]) => {
      if (!occupancyMap) return;
      const updatedOccupancies: Record<string, string[]> = {};
      Object.entries(occupancyMap).forEach(([occupancy, docs]) => {
        if (!Array.isArray(docs)) return;
        updatedOccupancies[occupancy] = docs.filter(
          (doc) => doc.trim() !== documentLabel.trim()
        );
      });
      next[estimationType as USER_LOAN_ESTIMATION_TYPE] = updatedOccupancies;
    });
    setDocumentationRequestsEdits((prev) => ({
      ...prev,
      [loanType]: next,
    }));
    setDocumentationRequestsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleCloseProgramModal = () => {
    setProgramModalOpen(false);
    dispatch(setSelectedLoanType(null));
    setReasonForChange('');
    setBridgeTermsEdits(null);
    setBridgeTermsDirty(false);
  };

  const handleBridgeTermsChange = (field: keyof BridgeTerms, value: number) => {
    setBridgeTermsEdits((prev) => ({
      ...(prev || defaultBridgeTerms),
      [field]: value,
    }));
    setBridgeTermsDirty(true);
  };

  const handleAddFeeItem = (loanType: LoanType) => {
    const next: FeeItem = {
      key: `fee_${Date.now()}`,
      label: 'New Fee',
      description: '',
      category: 'lender',
      sign: 'add',
      amountType: 'flat',
      amount: 0,
      includedInApr: true,
    };
    setFeeItemsEdits((prev) => ({
      ...prev,
      [loanType]: [...(prev[loanType] || []), next],
    }));
    setFeeItemsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleAddFeeFromCatalog = (loanType: LoanType, catalogKey: string) => {
    const catalogItem = CALCULATED_FEE_CATALOG.find(
      (item) => item.key === catalogKey
    );
    if (!catalogItem) return;

    const existing = feeItemsEdits[loanType] || [];
    const alreadyAdded = existing.some((fee) => fee.key === catalogItem.key);
    if (alreadyAdded) return;

    const next: FeeItem = {
      key: catalogItem.key,
      label: catalogItem.label,
      description: catalogItem.defaultDescription,
      category: catalogItem.category,
      sign: 'add',
      amountType: 'flat',
      amount: 0,
      includedInApr: catalogItem.defaultIncludedInApr,
    };

    setFeeItemsEdits((prev) => ({
      ...prev,
      [loanType]: [...existing, next],
    }));
    setFeeItemsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleUpdateFeeItem = (
    loanType: LoanType,
    index: number,
    patch: Partial<FeeItem>
  ) => {
    setFeeItemsEdits((prev) => {
      const list = [...(prev[loanType] || [])];
      const current = list[index];
      if (!current) return prev;
      list[index] = { ...current, ...patch };
      return { ...prev, [loanType]: list };
    });
    setFeeItemsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleRemoveFeeItem = (loanType: LoanType, index: number) => {
    setFeeItemsEdits((prev) => {
      const list = [...(prev[loanType] || [])];
      list.splice(index, 1);
      return { ...prev, [loanType]: list };
    });
    setFeeItemsDirty((prev) => ({ ...prev, [loanType]: true }));
  };

  const handleRateChange = async (
    rowIndex: number,
    colIndex: number,
    newRate: number
  ): Promise<boolean> => {
    if (!selectedLoanType || !reasonForChange.trim()) {
      alert('Please provide a reason for the rate change');
      return false;
    }

    try {
      await dispatch(
        updateRateMatrixAsync({
          loanType: selectedLoanType,
          rowIndex,
          colIndex,
          newRate,
          reason: reasonForChange.trim(),
          changedBy: 'Admin User',
        })
      ).unwrap();

      setReasonForChange('');
      return true;
    } catch (error) {
      console.error('Failed to stage rate update:', error);
      return false;
    }
  };

  const handleResetMatrix = async (): Promise<boolean> => {
    if (!selectedLoanType || !reasonForChange.trim()) {
      alert('Please provide a reason for the reset');
      return false;
    }

    try {
      await dispatch(
        resetRateMatrixAsync({
          loanType: selectedLoanType,
          reason: reasonForChange.trim(),
          changedBy: 'Admin User',
        })
      ).unwrap();

      setReasonForChange('');
      return true;
    } catch (error) {
      console.error('Failed to reset matrix:', error);
      return false;
    }
  };

  const handlePublishChanges = async () => {
    if (!latestRates?._id) {
      alert('Unable to publish: active configuration not loaded.');
      return;
    }

    const hasFeeChanges = Object.values(feeItemsDirty).some(Boolean);
    const hasRuleChanges = Object.values(calculatedFeeRulesDirty).some(Boolean);
    const hasDocsChanges = Object.values(documentationRequestsDirty).some(
      Boolean
    );
    const hasBridgeTermsChanges = bridgeTermsDirty;
    if (
      draftChanges.length === 0 &&
      !hasFeeChanges &&
      !hasRuleChanges &&
      !hasDocsChanges &&
      !hasBridgeTermsChanges
    ) {
      alert('No staged changes to publish.');
      return;
    }
    if (!reasonForChange.trim()) {
      alert('Please provide a reason for the change');
      return;
    }

    setIsPublishing(true);

    try {
      if (bridgeTermsDirty && bridgeTermsEdits) {
        await patchBridgeTerms({
          configId: latestRates._id,
          updates: bridgeTermsEdits,
          reason: reasonForChange.trim() || 'Updated via admin console',
        }).unwrap();
      }

      const groupedByLoanType = draftChanges.reduce<
        Record<LoanType, DraftChange[]>
      >((acc, change) => {
        if (!PROGRAM_KEY_MAP[change.loanType]) {
          return acc;
        }
        if (!acc[change.loanType]) {
          acc[change.loanType] = [];
        }
        acc[change.loanType].push(change);
        return acc;
      }, {} as Record<LoanType, DraftChange[]>);

      const loanTypes = Object.keys(groupedByLoanType) as LoanType[];

      // Include loanTypes that have fee edits even if no draft matrix changes exist
      (Object.keys(feeItemsDirty) as LoanType[]).forEach((lt) => {
        if (feeItemsDirty[lt] && !loanTypes.includes(lt)) {
          loanTypes.push(lt);
        }
      });
      (Object.keys(calculatedFeeRulesDirty) as LoanType[]).forEach((lt) => {
        if (calculatedFeeRulesDirty[lt] && !loanTypes.includes(lt)) {
          loanTypes.push(lt);
        }
      });
      (Object.keys(documentationRequestsDirty) as LoanType[]).forEach((lt) => {
        if (documentationRequestsDirty[lt] && !loanTypes.includes(lt)) {
          loanTypes.push(lt);
        }
      });

      if (loanTypes.length === 0) {
        if (hasBridgeTermsChanges) {
          setBridgeTermsEdits(null);
          setBridgeTermsDirty(false);
          setReasonForChange('');
          alert('Bridge rates published successfully.');
          return;
        }
        alert('No supported loan-type changes detected.');
        return;
      }

      for (const loanType of loanTypes) {
        const programKey = PROGRAM_KEY_MAP[loanType];
        const matrix = rateMatrices[loanType];

        if (!matrix || !programKey) {
          console.warn(
            `Skipping publish for ${loanType} — missing matrix or program key.`
          );
          continue;
        }

        const matrixValues = matrix.matrix.map((row) =>
          row.map((cell) => {
            const numValue = Number(cell.rate);
            if (!Number.isFinite(numValue)) {
              throw new Error(
                `Invalid rate value in ${loanType} matrix: ${cell.rate}`
              );
            }
            return numValue;
          })
        );

        if (
          (matrixValues.length === 0 || matrixValues[0]?.length === 0) &&
          loanType !== 'Cash'
        ) {
          console.warn(`Skipping publish for ${loanType} — matrix is empty.`);
          continue;
        }

        // Ensure values is a proper 2D array
        if (
          !Array.isArray(matrixValues) ||
          (matrixValues.length === 0 && loanType !== 'Cash')
        ) {
          console.warn(
            `Skipping publish for ${loanType} — invalid matrix structure.`
          );
          continue;
        }

        // Validate 2D array structure
        if (
          matrixValues.length > 0 &&
          !matrixValues.every((row) => Array.isArray(row))
        ) {
          console.error(
            `Invalid matrix structure for ${loanType}:`,
            matrixValues
          );
          throw new Error(
            `Invalid matrix structure for ${loanType} - must be a 2D array`
          );
        }

        const reasonSummary = (groupedByLoanType[loanType] || [])
          .map((change) => change.reason?.trim())
          .filter((text): text is string => Boolean(text && text.length > 0))
          .join('; ');

        console.log(`Publishing matrix for ${loanType}:`, {
          configId: latestRates._id,
          program: programKey,
          matrixSize: `${matrixValues.length}x${matrixValues[0]?.length || 0}`,
          firstRow: matrixValues[0],
        });

        try {
          const result = await patchProgramMatrixCell({
            configId: latestRates._id,
            program: programKey,
            values: matrixValues, // Explicitly send values array
            feeItems: feeItemsDirty[loanType]
              ? feeItemsEdits[loanType]
              : undefined,
            calculatedFeeRules: calculatedFeeRulesDirty[loanType]
              ? calculatedFeeRulesEdits[loanType]
              : undefined,
            documentationRequests: documentationRequestsDirty[loanType]
              ? documentationRequestsEdits[loanType]
              : undefined,
            reason:
              reasonSummary ||
              reasonForChange.trim() ||
              'Updated via admin console',
          }).unwrap();
          console.log(`Successfully published matrix for ${loanType}`);
        } catch (error: any) {
          console.error(`Error updating matrix for ${loanType}:`, error);
          console.error('Request payload:', {
            configId: latestRates._id,
            program: programKey,
            values: matrixValues,
            valuesType: typeof matrixValues,
            valuesIsArray: Array.isArray(matrixValues),
          });
          throw new Error(
            `Failed to update ${loanType} matrix: ${
              error?.data?.message || error?.message || 'Unknown error'
            }`
          );
        }
      }

      dispatch(clearDraftChanges());
      setFeeItemsEdits({});
      setFeeItemsDirty({});
      setCalculatedFeeRulesEdits({});
      setCalculatedFeeRulesDirty({});
      setDocumentationRequestsEdits({});
      setDocumentationRequestsDirty({});
      setBridgeTermsEdits(null);
      setBridgeTermsDirty(false);
      alert('Rate matrices published successfully.');
    } catch (err) {
      console.error('Failed to publish draft changes:', err);
      alert('Failed to publish draft changes. Check console for details.');
    } finally {
      setIsPublishing(false);
    }
  };

  // Helper function to format rates to 3 significant digits
  const formatRate = (rate: number) => {
    return rate.toPrecision(4);
  };

  const getTrendIcon = (value: number) => {
    if (value > 0) return <TrendingUpIcon color="error" />;
    if (value < 0) return <TrendingDownIcon color="success" />;
    return <TrendingFlatIcon color="action" />;
  };

  const getTrendColor = (value: number) => {
    if (value > 0) return 'error';
    if (value < 0) return 'success';
    return 'default';
  };

  // Do not gate the whole page on `interestRate.loading`: matrix/APR mutations
  // set that flag and would unmount this tree (including the edit dialog).
  if (latestRatesLoading || statsLoading) {
    return (
      <Container sx={{ py: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Interest Rate Configuration...
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            color="primary.main"
            gutterBottom
          >
            Rates
          </Typography>
          <Typography variant="body1" color="text.primary">
            Manage and configure interest rates for all loan programs
          </Typography>
        </Box>
      </Box>

      <MarketRatesPanel />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      {/* Loan Programs Section */}
      {(() => {
        const loanTypesInOrder = (
          [
            'Conventional',
            'FHA',
            'VA',
            'USDA',
            'HELOC',
            'HELOAN',
            'SecondMortgage',
            'DSCR',
            'Bridge',
            'FixAndFlip',
            'Commercial',
          ] as LoanType[]
        ).filter((lt) => Boolean(rateMatrices[lt]));

        const fallbackLoanType = (loanTypesInOrder[0] ||
          'Conventional') as LoanType;
        const loanType = loanTypesInOrder.includes(activeMatrixLoanType)
          ? activeMatrixLoanType
          : fallbackLoanType;

        const isBridgeProgram = loanType === 'Bridge';
        const matrix = rateMatrices[loanType];
        const config = RATE_TABLE_CONFIGS[loanType];
        const validation = validateRateMatrix(matrix);
        const bridgeTerms = latestRates?.bridgeTerms || defaultBridgeTerms;
        const bridgeLastUpdated = latestRates?.metadata?.publishedAt
          ? new Date(latestRates.metadata.publishedAt).toLocaleString()
          : latestRates?.updatedAt
          ? new Date(latestRates.updatedAt).toLocaleString()
          : 'Unknown';

        return (
          <Box>
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2,
                pb: 1,
              }}
            >
              {loanTypesInOrder.map((lt) => (
                <Button
                  key={lt}
                  onClick={() => setActiveMatrixLoanType(lt)}
                  variant="text"
                  sx={{
                    px: 0,
                    borderRadius: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontWeight: lt === loanType ? 700 : 500,
                    borderBottom:
                      lt === loanType ? '2px solid' : '2px solid transparent',
                    borderColor:
                      lt === loanType ? 'primary.main' : 'transparent',
                  }}
                >
                  {lt}
                </Button>
              ))}
            </Box>

            <Card>
              <CardHeader
                title={
                  isBridgeProgram
                    ? `${loanType} Terms`
                    : `${loanType} Rate Matrix`
                }
                subheader={
                  isBridgeProgram
                    ? `Last updated: ${bridgeLastUpdated}`
                    : `Last updated: ${new Date(
                        matrix.lastUpdated
                      ).toLocaleString()}`
                }
                subheaderTypographyProps={{ sx: { color: 'white' } }}
                action={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => handleOpenProgramModal(loanType)}
                      size="small"
                    >
                      Edit This Program
                    </Button>
                  </Box>
                }
              />
              <CardContent>
                {isBridgeProgram ? (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Term
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 'bold' }}
                              align="center"
                            >
                              Interest Rate (%)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[
                            { label: 'Base Rate', value: bridgeTerms.baseRate },
                            { label: '6 Months', value: bridgeTerms.sixMonth },
                            {
                              label: '12 Months',
                              value: bridgeTerms.twelveMonth,
                            },
                            {
                              label: '18 Months',
                              value: bridgeTerms.eighteenMonth,
                            },
                            {
                              label: '24 Months',
                              value: bridgeTerms.twentyFourMonth,
                            },
                          ].map((row) => (
                            <TableRow key={row.label}>
                              <TableCell>{row.label}</TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="primary"
                                >
                                  {row.value.toFixed(2)}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              {config.yAxis.label} \ {config.xAxis.label}
                            </TableCell>
                            {config.xAxis.values.map((value, index) => (
                              <TableCell
                                key={index}
                                align="center"
                                sx={{ fontWeight: 'bold' }}
                              >
                                <Typography variant="caption" display="block">
                                  {value}
                                </Typography>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {matrix.matrix.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                <Typography variant="caption">
                                  {config.yAxis.values[rowIndex]}
                                </Typography>
                              </TableCell>
                              {row.map((cell, colIndex) => (
                                <TableCell key={colIndex} align="center">
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color="primary"
                                  >
                                    {cell.rate.toFixed(2)}%
                                  </Typography>
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {config.yAxis.label} \ {config.xAxis.label}
                          </TableCell>
                          {config.xAxis.values.map((value, index) => (
                            <TableCell
                              key={index}
                              align="center"
                              sx={{ fontWeight: 'bold' }}
                            >
                              <Typography variant="caption" display="block">
                                {value}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {matrix.matrix.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              <Typography variant="caption">
                                {config.yAxis.values[rowIndex]}
                              </Typography>
                            </TableCell>
                            {row.map((cell, colIndex) => (
                              <TableCell key={colIndex} align="center">
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="primary"
                                >
                                  {cell.rate.toFixed(2)}%
                                </Typography>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {(() => {
                  const docsByType = getProgramDocumentationRequests(loanType);
                  const uniqueDocs = getUniqueDocumentationRequests(docsByType);
                  if (uniqueDocs.length === 0) return null;
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Documentation Requests (click to remove)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {uniqueDocs.map((doc) => (
                          <Chip
                            key={doc}
                            label={doc}
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              handleRemoveDocumentationRequest(loanType, doc)
                            }
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </Box>
        );
      })()}
      {latestRates && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Active Version {latestRates.version} • Updated{' '}
              {latestRates.metadata?.updatedBy || 'Unknown'} on{' '}
              {latestRates.metadata?.publishedAt
                ? new Date(latestRates.metadata.publishedAt).toLocaleString()
                : new Date(latestRates.updatedAt).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Change Reason:{' '}
              {latestRates.metadata?.reason || 'No reason provided'}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Program Rate Matrix Modal */}
      <Dialog
        open={programModalOpen}
        onClose={handleCloseProgramModal}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>Edit Rates - {selectedLoanType}</DialogTitle>
        <DialogContent>
          {selectedLoanType && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Reason for Rate Change"
                value={reasonForChange}
                onChange={(e) => setReasonForChange(e.target.value)}
                placeholder="Enter the reason for rate changes (e.g., market conditions, policy update)"
                sx={{ mb: 3 }}
              />

              {/* Folder-style categories */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={editRatesFolderTab}
                  onChange={(_, newValue) => setEditRatesFolderTab(newValue)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  aria-label="Edit rate categories"
                >
                  <Tab
                    id="edit-rates-dialog-tab-0"
                    aria-controls="edit-rates-dialog-tabpanel-0"
                    icon={<Speed />}
                    iconPosition="start"
                    label="Interest Rates"
                  />
                  <Tab
                    id="edit-rates-dialog-tab-1"
                    aria-controls="edit-rates-dialog-tabpanel-1"
                    icon={<Calculate />}
                    iconPosition="start"
                    label="Closing Costs"
                  />
                  <Tab
                    id="edit-rates-dialog-tab-2"
                    aria-controls="edit-rates-dialog-tabpanel-2"
                    icon={<Description />}
                    iconPosition="start"
                    label="Documentation"
                  />
                </Tabs>
              </Box>

              <TabPanel value={editRatesFolderTab} index={0}>
                {selectedLoanType === 'Cash' ? (
                  <Alert severity="info">
                    Cash programs do not have interest rates or rate matrices.
                  </Alert>
                ) : selectedLoanType === 'Bridge' ? (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Bridge rates use term-specific base rates plus the LTV
                        and credit score matrix adjustments below.
                      </Alert>
                      <Grid container spacing={2}>
                        {(() => {
                          const terms = bridgeTermsEdits || defaultBridgeTerms;
                          return [
                            {
                              key: 'baseRate',
                              label: 'Base Rate (%)',
                              value: terms.baseRate,
                            },
                            {
                              key: 'sixMonth',
                              label: '6 Month Rate (%)',
                              value: terms.sixMonth,
                            },
                            {
                              key: 'twelveMonth',
                              label: '12 Month Rate (%)',
                              value: terms.twelveMonth,
                            },
                            {
                              key: 'eighteenMonth',
                              label: '18 Month Rate (%)',
                              value: terms.eighteenMonth,
                            },
                            {
                              key: 'twentyFourMonth',
                              label: '24 Month Rate (%)',
                              value: terms.twentyFourMonth,
                            },
                          ].map((term) => (
                            <Grid item xs={12} sm={6} md={4} key={term.key}>
                              <TextField
                                label={term.label}
                                type="number"
                                value={term.value}
                                onChange={(e) =>
                                  handleBridgeTermsChange(
                                    term.key as keyof BridgeTerms,
                                    parseFloat(e.target.value)
                                  )
                                }
                                inputProps={{ step: 0.01, min: 0, max: 30 }}
                                fullWidth
                              />
                            </Grid>
                          ));
                        })()}
                      </Grid>
                    </Box>
                    <RateMatrixEditor
                      loanType={selectedLoanType}
                      matrix={rateMatrices[selectedLoanType]}
                      onRateChange={handleRateChange}
                      onReset={handleResetMatrix}
                      busy={loading}
                    />
                  </Box>
                ) : (
                  <RateMatrixEditor
                    loanType={selectedLoanType}
                    matrix={rateMatrices[selectedLoanType]}
                    onRateChange={handleRateChange}
                    onReset={handleResetMatrix}
                    busy={loading}
                  />
                )}
              </TabPanel>

              <TabPanel value={editRatesFolderTab} index={2}>
                {(() => {
                  const docsByType =
                    documentationRequestsEdits[selectedLoanType] || {};
                  const loanProgram =
                    LOAN_TYPE_TO_USER_PROGRAM[selectedLoanType];
                  const normalizedDocs = mergeProgramDocumentationRequests(
                    loanProgram,
                    docsByType
                  );
                  const estimationTypes = Object.keys(
                    normalizedDocs
                  ) as USER_LOAN_ESTIMATION_TYPE[];

                  if (estimationTypes.length === 0) {
                    return (
                      <Alert severity="info">
                        No documentation defaults found for this program.
                      </Alert>
                    );
                  }

                  return (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Driver&apos;s license or government issued ID is always
                        required and is not shown in these lists.
                      </Alert>
                      {estimationTypes.map((estimationType) => {
                        const occupancyDocs =
                          normalizedDocs[estimationType] || {};
                        const occupancies = Object.keys(
                          occupancyDocs
                        ) as (keyof typeof occupancyDocs)[];

                        return (
                          <Card
                            variant="outlined"
                            sx={{ mb: 3 }}
                            key={estimationType}
                          >
                            <CardHeader
                              title={`${userLoanEstimationTypeMap[estimationType]} Documentation`}
                              subheader="One item per line"
                              action={
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    handleResetDocumentationRequests(
                                      selectedLoanType,
                                      estimationType
                                    )
                                  }
                                >
                                  Reset to Defaults
                                </Button>
                              }
                            />
                            <CardContent>
                              <Grid container spacing={2}>
                                {occupancies.map((occupancy) => (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={occupancy as string}
                                  >
                                    <TextField
                                      label={`${
                                        userOccupancyMap[
                                          occupancy as keyof typeof userOccupancyMap
                                        ] || occupancy
                                      } Docs`}
                                      multiline
                                      minRows={3}
                                      fullWidth
                                      value={(
                                        ((
                                          docsByType?.[estimationType] as any
                                        )?.[occupancy] ||
                                          (occupancyDocs as any)[occupancy] ||
                                          []) as string[]
                                      ).join('\\n')}
                                      onChange={(e) =>
                                        handleUpdateDocumentationRequests(
                                          selectedLoanType,
                                          estimationType,
                                          occupancy as string,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  );
                })()}
              </TabPanel>

              <TabPanel value={editRatesFolderTab} index={1}>
                {(() => {
                  const fees = feeItemsEdits[selectedLoanType] || [];
                  const rules = calculatedFeeRulesEdits[selectedLoanType] || [];
                  return (
                    <>
                      <Card variant="outlined" sx={{ mb: 3 }}>
                        <CardHeader
                          title="Closing Cost Fees (Per Program)"
                          action={
                            <Button
                              variant="contained"
                              startIcon={<AddCircle />}
                              onClick={() => handleAddFeeItem(selectedLoanType)}
                            >
                              Add Fee
                            </Button>
                          }
                        />
                        <CardContent>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Fee Library
                            </Typography>
                            <Box
                              sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                            >
                              <FormControl sx={{ minWidth: 280 }}>
                                <InputLabel id="fee-library-label">
                                  Select fee
                                </InputLabel>
                                <Select
                                  labelId="fee-library-label"
                                  label="Select fee"
                                  value={feeLibrarySelection}
                                  onChange={(e) =>
                                    setFeeLibrarySelection(e.target.value)
                                  }
                                >
                                  {CALCULATED_FEE_CATALOG.map((fee) => (
                                    <MenuItem key={fee.key} value={fee.key}>
                                      {fee.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  if (!feeLibrarySelection) return;
                                  handleAddFeeFromCatalog(
                                    selectedLoanType,
                                    feeLibrarySelection
                                  );
                                  setFeeLibrarySelection('');
                                }}
                              >
                                Add from Library
                              </Button>
                            </Box>
                          </Box>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Calculated Fees (Visibility + APR)
                            </Typography>
                            <Grid container spacing={1}>
                              {rules.map((r) => (
                                <Grid item xs={12} key={r.key}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 2,
                                      p: 1,
                                      border:
                                        '1px solid rgba(255,255,255,0.15)',
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography sx={{ flex: 1 }}>
                                      {r.label}
                                    </Typography>
                                    <TextField
                                      label="Description"
                                      value={r.description || ''}
                                      onChange={(e) =>
                                        handleUpdateCalculatedRule(
                                          selectedLoanType,
                                          r.key,
                                          { description: e.target.value }
                                        )
                                      }
                                      size="small"
                                      sx={{ minWidth: 260 }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={!!r.visible}
                                          onChange={(e) =>
                                            handleUpdateCalculatedRule(
                                              selectedLoanType,
                                              r.key,
                                              { visible: e.target.checked }
                                            )
                                          }
                                        />
                                      }
                                      label="Visible"
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={!!r.includedInApr}
                                          onChange={(e) =>
                                            handleUpdateCalculatedRule(
                                              selectedLoanType,
                                              r.key,
                                              {
                                                includedInApr: e.target.checked,
                                              }
                                            )
                                          }
                                        />
                                      }
                                      label="Included in APR"
                                    />
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>

                          <Divider sx={{ my: 2, opacity: 0.2 }} />
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            Adjustments (Add/Subtract)
                          </Typography>
                          {fees.length === 0 ? (
                            <Alert severity="info">
                              No fees configured yet for this program. Add fees
                              above.
                            </Alert>
                          ) : (
                            <Grid container spacing={2}>
                              {fees.map((fee, idx) => (
                                <React.Fragment key={fee.key || idx}>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      label="Label"
                                      value={fee.label}
                                      onChange={(e) =>
                                        handleUpdateFeeItem(
                                          selectedLoanType,
                                          idx,
                                          {
                                            label: e.target.value,
                                          }
                                        )
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      label="Description"
                                      value={fee.description || ''}
                                      onChange={(e) =>
                                        handleUpdateFeeItem(
                                          selectedLoanType,
                                          idx,
                                          {
                                            description: e.target.value,
                                          }
                                        )
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Add/Subtract"
                                      value={fee.sign}
                                      onChange={(e) =>
                                        handleUpdateFeeItem(
                                          selectedLoanType,
                                          idx,
                                          {
                                            sign: e.target.value as FeeSign,
                                          }
                                        )
                                      }
                                      SelectProps={{ native: true }}
                                    >
                                      <option value="add">Add</option>
                                      <option value="subtract">Subtract</option>
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Amount Type"
                                      value={fee.amountType}
                                      onChange={(e) =>
                                        handleUpdateFeeItem(
                                          selectedLoanType,
                                          idx,
                                          {
                                            amountType: e.target
                                              .value as FeeAmountType,
                                          }
                                        )
                                      }
                                      SelectProps={{ native: true }}
                                    >
                                      <option value="flat">$ Flat</option>
                                      <option value="percentOfLoan">
                                        % of Loan
                                      </option>
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      label={
                                        fee.amountType === 'percentOfLoan'
                                          ? 'Amount (%)'
                                          : 'Amount ($)'
                                      }
                                      value={fee.amount}
                                      onChange={(e) =>
                                        handleUpdateFeeItem(
                                          selectedLoanType,
                                          idx,
                                          {
                                            amount: Number(e.target.value),
                                          }
                                        )
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Category"
                                      value={fee.category}
                                      onChange={(e) =>
                                        handleUpdateFeeItem(
                                          selectedLoanType,
                                          idx,
                                          {
                                            category: e.target
                                              .value as FeeCategory,
                                          }
                                        )
                                      }
                                      SelectProps={{ native: true }}
                                    >
                                      <option value="lender">Lender</option>
                                      <option value="title">Title</option>
                                      <option value="government">
                                        Government
                                      </option>
                                      <option value="prepaid">Prepaid</option>
                                      <option value="other">Other</option>
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={fee.includedInApr}
                                          onChange={(e) =>
                                            handleUpdateFeeItem(
                                              selectedLoanType,
                                              idx,
                                              {
                                                includedInApr: e.target.checked,
                                              }
                                            )
                                          }
                                        />
                                      }
                                      label="Included in APR"
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      startIcon={<Delete />}
                                      onClick={() =>
                                        handleRemoveFeeItem(
                                          selectedLoanType,
                                          idx
                                        )
                                      }
                                    >
                                      Remove
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Divider sx={{ opacity: 0.2 }} />
                                  </Grid>
                                </React.Fragment>
                              ))}
                            </Grid>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}
              </TabPanel>

              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color={draftChanges.length ? 'warning.main' : 'text.primary'}
                >
                  Staged changes: {draftChanges.length}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePublishChanges}
                  disabled={
                    (draftChanges.length === 0 &&
                      !feeItemsDirty[selectedLoanType] &&
                      !calculatedFeeRulesDirty[selectedLoanType] &&
                      !documentationRequestsDirty[selectedLoanType] &&
                      !bridgeTermsDirty) ||
                    isPublishing
                  }
                  startIcon={<CheckCircle />}
                >
                  {isPublishing ? 'Publishing…' : 'Publish Changes'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProgramModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Rate Matrix Editor Component
interface RateMatrixEditorProps {
  loanType: LoanType;
  matrix: RateMatrix;
  onRateChange: (
    rowIndex: number,
    colIndex: number,
    newRate: number
  ) => Promise<boolean>;
  onReset: () => Promise<boolean>;
  busy?: boolean;
}

const RateMatrixEditor: React.FC<RateMatrixEditorProps> = ({
  loanType,
  matrix,
  onRateChange,
  onReset,
  busy = false,
}) => {
  const config = RATE_TABLE_CONFIGS[loanType];
  const [editingCell, setEditingCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleCellClick = (
    rowIndex: number,
    colIndex: number,
    currentRate: number
  ) => {
    if (busy) return;
    setEditingCell({ row: rowIndex, col: colIndex });
    setEditValue(String(currentRate));
  };

  const handleCellSave = async () => {
    if (!editingCell || busy) return;
    const newRate = parseFloat(editValue);
    if (Number.isNaN(newRate) || newRate < 0 || newRate > 25) return;
    const ok = await onRateChange(editingCell.row, editingCell.col, newRate);
    if (ok) {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleResetClick = async () => {
    if (busy) return;
    const ok = await onReset();
    if (ok) {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const formatRate = (rate: number) => {
    const n = Number(rate);
    if (!Number.isFinite(n)) return '—';
    return `${n.toPrecision(4)}%`;
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {config.xAxis.label} × {config.yAxis.label} Rate Matrix
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="warning"
            disabled={busy}
            onClick={() => void handleResetClick()}
          >
            Reset to Defaults
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {config.yAxis.label} \ {config.xAxis.label}
              </TableCell>
              {config.xAxis.values.map((value, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{ fontWeight: 'bold' }}
                >
                  <Typography variant="caption" display="block">
                    {value}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {matrix.matrix.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Typography variant="caption">
                    {config.yAxis.values[rowIndex]}
                  </Typography>
                </TableCell>
                {row.map((cell, colIndex) => (
                  <TableCell key={colIndex} align="center">
                    {editingCell?.row === rowIndex &&
                    editingCell?.col === colIndex ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <TextField
                          size="small"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleCellSave();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              handleCellCancel();
                            }
                          }}
                          autoFocus
                          disabled={busy}
                          inputProps={{
                            style: { textAlign: 'center' },
                            step: 0.01,
                            min: 0,
                            max: 25,
                          }}
                        />
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => void handleCellSave()}
                          aria-label="Save rate"
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          handleCellClick(rowIndex, colIndex, Number(cell.rate))
                        }
                        sx={{ minWidth: '60px' }}
                      >
                        {formatRate(cell.rate as number)}
                      </Button>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
